import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { fmtEUR, fmtDate, todayISO } from "../lib/format";
import { PageHeader } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Download, Pencil, Trash2, ClipboardList, MapPin } from "lucide-react";
import { generateTransportOrderPDF } from "../lib/pdf";
import { toast } from "sonner";

const blank = {
  client_id: "none", client_name: "", client_phone: "", client_email: "", nb_passengers: 1,
  vehicle_marque_modele: "", vehicle_immatriculation: "", vehicle_carte_pro: "",
  date_reservation: todayISO(), date_prise_en_charge: todayISO(), heure_prise_en_charge: "",
  adresse_prise_en_charge: "", destination: "", arrets: "", tarif: 0, tarif_mention: "",
  chauffeur_nom: "", chauffeur_carte_pro: "",
  reservation_par: "", reservation_datetime: "", reservation_moyen: "Téléphone",
  notes: "",
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">{label && <Label className="text-xs">{label}</Label>}{children}</div>
);

export default function TransportOrders() {
  const qc = useQueryClient();
  const { data: orders = [] } = useQuery({ queryKey: ["transport-orders"], queryFn: api.listTransportOrders });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: api.listClients });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: api.getSettings });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [toDelete, setToDelete] = useState(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ["transport-orders"] });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({ ...blank, ...editing, client_id: editing.client_id || "none" });
    } else {
      setForm({
        ...blank,
        vehicle_marque_modele: settings?.vehicle_marque_modele || "",
        vehicle_immatriculation: settings?.vehicle_immatriculation || "",
        vehicle_carte_pro: settings?.chauffeur_carte_pro || "",
        chauffeur_nom: settings?.chauffeur_nom || "",
        chauffeur_carte_pro: settings?.chauffeur_carte_pro || "",
      });
    }
  }, [open, editing, settings]);

  const pickClient = (id) => {
    if (id === "none") { set("client_id", "none"); return; }
    const c = clients.find((x) => x.id === id);
    setForm((p) => ({ ...p, client_id: id, client_name: c?.name || "", client_phone: c?.phone || "", client_email: c?.email || "" }));
  };

  const save = async () => {
    if (!form.client_name && form.client_id === "none") return toast.error("Indiquez le client");
    if (!form.adresse_prise_en_charge || !form.destination) return toast.error("Prise en charge et destination requises");
    const payload = {
      ...form,
      client_id: form.client_id === "none" ? null : form.client_id,
      nb_passengers: Number(form.nb_passengers || 1),
      tarif: Number(form.tarif || 0),
    };
    try {
      if (editing) await api.updateTransportOrder(editing.id, payload);
      else await api.createTransportOrder(payload);
      setOpen(false);
      refresh();
      toast.success(editing ? "Bon de transport modifié" : "Bon de transport créé");
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const confirmDelete = async () => {
    await api.deleteTransportOrder(toDelete.id);
    setToDelete(null);
    refresh();
    toast.success("Bon de transport supprimé");
  };

  return (
    <div>
      <PageHeader
        title="Bons de transport"
        subtitle={`${orders.length} bon(s) de transport VTC`}
        action={
          <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-slate-900 hover:bg-slate-800" data-testid="create-order-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Nouveau bon
          </Button>
        }
      />

      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <th className="px-4 py-3 font-semibold">N° Bon</th>
                <th className="px-4 py-3 font-semibold">Client</th>
                <th className="px-4 py-3 font-semibold">Prise en charge</th>
                <th className="px-4 py-3 font-semibold">Trajet</th>
                <th className="px-4 py-3 font-semibold">Chauffeur</th>
                <th className="px-4 py-3 font-semibold text-right">Tarif</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.length ? orders.map((o) => (
                <tr key={o.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`order-row-${o.number}`}>
                  <td className="px-4 py-3 font-mono text-slate-700">{o.number}</td>
                  <td className="px-4 py-3 font-medium">{o.client_name}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600">{fmtDate(o.date_prise_en_charge)}{o.heure_prise_en_charge && <span className="text-slate-400"> · {o.heure_prise_en_charge}</span>}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 text-slate-700"><MapPin className="h-3.5 w-3.5 text-[#C5A059]" />{o.adresse_prise_en_charge}</div>
                    <div className="flex items-center gap-1.5 text-slate-500 text-xs mt-0.5"><MapPin className="h-3.5 w-3.5 text-slate-400" />{o.destination}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{o.chauffeur_nom || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono">{o.tarif_mention ? <span className="text-xs italic text-slate-500">{o.tarif_mention}</span> : fmtEUR(o.tarif)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => { generateTransportOrderPDF(o, settings || {}); toast.success(`PDF ${o.number} téléchargé`); }} title="Télécharger PDF" data-testid={`download-order-${o.number}`}><Download className="h-4 w-4 text-slate-600" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => { setEditing(o); setOpen(true); }} data-testid={`edit-order-${o.number}`}><Pencil className="h-4 w-4 text-slate-600" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => setToDelete(o)} data-testid={`delete-order-${o.number}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400"><ClipboardList className="h-8 w-8 mx-auto mb-2 opacity-40" />Aucun bon de transport</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col gap-0 p-0" data-testid="order-dialog">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
            <DialogTitle className="font-display text-2xl">{editing ? "Modifier le bon de transport" : "Nouveau bon de transport"}</DialogTitle>
            <DialogDescription>Le bon de transport est émis au nom de Fun Drive (SIRET 105 700 348 00011). L'adresse et le N° REVTC sont repris depuis vos Paramètres.</DialogDescription>
          </DialogHeader>

          <div className="px-6 py-5 overflow-y-auto flex-1 space-y-6">
            {/* Véhicule */}
            <div>
              <div className="text-sm font-semibold text-slate-800 mb-2">Véhicule</div>
              <div className="grid sm:grid-cols-3 gap-3">
                <Field label="Marque et modèle"><Input value={form.vehicle_marque_modele} onChange={(e) => set("vehicle_marque_modele", e.target.value)} data-testid="order-vehicle-model" placeholder="Mercedes Classe E" /></Field>
                <Field label="Immatriculation"><Input value={form.vehicle_immatriculation} onChange={(e) => set("vehicle_immatriculation", e.target.value)} data-testid="order-vehicle-plate" placeholder="AB-123-CD" /></Field>
                <Field label="N° carte pro chauffeur"><Input value={form.vehicle_carte_pro} onChange={(e) => set("vehicle_carte_pro", e.target.value)} /></Field>
              </div>
            </div>

            {/* Client */}
            <div>
              <div className="text-sm font-semibold text-slate-800 mb-2">Client</div>
              <div className="grid sm:grid-cols-3 gap-3">
                <Field label="Client existant (optionnel)">
                  <Select value={form.client_id} onValueChange={pickClient}>
                    <SelectTrigger data-testid="order-client-select"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Saisie manuelle</SelectItem>
                      {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Nom / entreprise"><Input value={form.client_name} onChange={(e) => set("client_name", e.target.value)} data-testid="order-client-name" /></Field>
                <Field label="Nombre de passagers"><Input type="number" min="1" value={form.nb_passengers} onChange={(e) => set("nb_passengers", e.target.value)} data-testid="order-passengers" /></Field>
                <Field label="Téléphone"><Input value={form.client_phone} onChange={(e) => set("client_phone", e.target.value)} /></Field>
                <Field label="E-mail"><Input value={form.client_email} onChange={(e) => set("client_email", e.target.value)} /></Field>
              </div>
            </div>

            {/* Course */}
            <div>
              <div className="text-sm font-semibold text-slate-800 mb-2">Course</div>
              <div className="grid sm:grid-cols-3 gap-3">
                <Field label="Date de réservation"><Input type="date" value={form.date_reservation} onChange={(e) => set("date_reservation", e.target.value)} /></Field>
                <Field label="Date de prise en charge"><Input type="date" value={form.date_prise_en_charge} onChange={(e) => set("date_prise_en_charge", e.target.value)} data-testid="order-pickup-date" /></Field>
                <Field label="Heure de prise en charge"><Input type="time" value={form.heure_prise_en_charge} onChange={(e) => set("heure_prise_en_charge", e.target.value)} data-testid="order-pickup-time" /></Field>
                <div className="sm:col-span-3"><Field label="Adresse de prise en charge"><Input value={form.adresse_prise_en_charge} onChange={(e) => set("adresse_prise_en_charge", e.target.value)} data-testid="order-pickup-address" placeholder="Aéroport Marseille Provence, T1" /></Field></div>
                <div className="sm:col-span-3"><Field label="Destination"><Input value={form.destination} onChange={(e) => set("destination", e.target.value)} data-testid="order-destination" placeholder="Gare Saint-Charles, Marseille" /></Field></div>
                <div className="sm:col-span-3"><Field label="Arrêts intermédiaires (optionnel)"><Input value={form.arrets} onChange={(e) => set("arrets", e.target.value)} placeholder="Ex : passage hôtel, ..." /></Field></div>
                <Field label="Tarif convenu (€)"><Input type="number" step="0.01" value={form.tarif} onChange={(e) => set("tarif", e.target.value)} data-testid="order-tarif" /></Field>
                <div className="sm:col-span-2"><Field label="ou mention"><Input value={form.tarif_mention} onChange={(e) => set("tarif_mention", e.target.value)} placeholder="selon devis / réservation" /></Field></div>
              </div>
            </div>

            {/* Chauffeur */}
            <div>
              <div className="text-sm font-semibold text-slate-800 mb-2">Chauffeur</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Nom et prénom"><Input value={form.chauffeur_nom} onChange={(e) => set("chauffeur_nom", e.target.value)} data-testid="order-driver-name" /></Field>
                <Field label="N° carte professionnelle VTC"><Input value={form.chauffeur_carte_pro} onChange={(e) => set("chauffeur_carte_pro", e.target.value)} data-testid="order-driver-card" /></Field>
              </div>
            </div>

            {/* Réservation */}
            <div>
              <div className="text-sm font-semibold text-slate-800 mb-2">Réservation</div>
              <div className="grid sm:grid-cols-3 gap-3">
                <Field label="Réservé par"><Input value={form.reservation_par} onChange={(e) => set("reservation_par", e.target.value)} data-testid="order-reserved-by" /></Field>
                <Field label="Date et heure de réservation"><Input type="datetime-local" value={form.reservation_datetime} onChange={(e) => set("reservation_datetime", e.target.value)} /></Field>
                <Field label="Moyen de réservation">
                  <Select value={form.reservation_moyen} onValueChange={(v) => set("reservation_moyen", v)}>
                    <SelectTrigger data-testid="order-reservation-channel"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Téléphone">Téléphone</SelectItem>
                      <SelectItem value="Site internet">Site internet</SelectItem>
                      <SelectItem value="E-mail">E-mail</SelectItem>
                      <SelectItem value="Plateforme">Plateforme</SelectItem>
                      <SelectItem value="Sur place">Sur place</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </div>

            <Field label="Notes"><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
          </div>

          <DialogFooter className="px-6 py-4 border-t border-slate-100 bg-white">
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="save-order-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le bon de transport ?</AlertDialogTitle>
            <AlertDialogDescription>{toDelete?.number} sera définitivement supprimé.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-order">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
